/**
 * Loans & advances — the operational side of `PayrollLoanEwaSettings`' policy configuration.
 *
 * TWO STAGES, TWO ROWS. A `PayrollLoanRequest` is the application (draft → submitted → recommended
 * → approved/rejected/withdrawn), decided through `@hrms/workflow` like any other approval — see
 * `PAYROLL_LOAN_REQUEST_STATUSES`. Only an `approved` request ever produces a `PayrollLoan` — the
 * confirmed, disbursed thing with its own instalment schedule. Approving a request authorises the
 * money; it does not move it, the same split `expense.contract.ts` already draws between a claim's
 * `approved` and `settled`.
 *
 * INTEREST-FREE. `PayrollLoanEwaSettings` carries no interest-rate field, and the reference product
 * this was scoped from shows "0.0% (Corp)" — every instalment is `principal / tenureMonths`, floored
 * to the minor unit, with the remainder folded into the final one: ₹2,00,000 over 12 months is
 * eleven instalments of ₹16,666.66 and a final one of ₹16,666.74, computed by
 * `@hrms/payroll`'s `computeLoanInstallments`.
 *
 * RECOVERY IS NEVER INFERRED FROM A DRAFT RUN. An instalment moves from `scheduled` to `recovered`
 * only when the payroll run that carries it actually commits — a later phase, once the statutory
 * computation engine exists. Adding an instalment to a draft run, or that run being reopened
 * afterward, must never touch `PayrollLoan.outstandingPrincipal` on its own. See
 * `payroll-inputs.contract.ts` and `PayrollRun` in `payroll-calendar.contract.ts` for the run-lock
 * side of this.
 *
 * Implemented by `apps/api/src/routes/payroll-loans.ts`. Approving a request creates the loan in
 * `pending_disbursement`; `disburseLoan` is what generates the instalment schedule.
 */

import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import {
  currencySchema,
  decimalStringSchema,
  errorBodySchema,
  idempotencyKeySchema,
  instantSchema,
  orgIdSchema,
  paginatedSchema,
  paginationQuerySchema,
  payrollLoanInstallmentStatusSchema,
  payrollLoanRequestStatusSchema,
  payrollLoanStatusSchema,
  uuidSchema,
} from '../schemas/common.js';
import { routeMeta } from './meta.js';

const c = initContract();

/** A loan amount must be a positive figure: zero or negative would produce a loan that cannot be
 * scheduled (or, worse, one that pays the employee back). */
const positiveAmountSchema = decimalStringSchema.refine(
  (value) => !value.startsWith('-') && /[1-9]/.test(value),
  'Must be greater than zero',
);

const idempotencyHeadersSchema = z.object({ 'idempotency-key': idempotencyKeySchema });

// ---------------------------------------------------------------------------
// Requests
// ---------------------------------------------------------------------------

export const payrollLoanRequestSchema = z.object({
  id: uuidSchema,
  orgId: orgIdSchema,
  /** Allocated on submit (e.g. LN-2026-00042); null on a draft. The loan it produces reuses it. */
  reference: z.string().nullable(),
  employmentId: uuidSchema,
  employeeName: z.string(),
  employeeCode: z.string().nullable(),
  /** A free label, e.g. "Personal Emergency" — there is no loan-type catalogue to validate against,
   * matching how `ExpenseCategory` is a configured list but this is not (yet). */
  loanType: z.string(),
  currency: currencySchema,
  requestedAmount: decimalStringSchema,
  requestedTenureMonths: z.number().int().min(1).max(60),
  reason: z.string().nullable(),
  status: payrollLoanRequestStatusSchema,
  managerNote: z.string().nullable(),
  decidedAt: instantSchema.nullable(),
  decidedByName: z.string().nullable(),
  decisionNote: z.string().nullable(),
  /** Set once `approved` produces the confirmed loan. */
  loanId: uuidSchema.nullable(),
  workflowInstanceId: uuidSchema.nullable(),
  createdAt: instantSchema,
  version: z.number().int().positive(),
});
export type PayrollLoanRequest = z.infer<typeof payrollLoanRequestSchema>;

export const createLoanRequestBodySchema = z.object({
  /** Omitted = self. Set only by someone with `loan:manage` filing on an employee's behalf, the
   * same on-behalf override `expense.contract.ts`'s `createClaim` already uses. */
  employmentId: uuidSchema.optional(),
  loanType: z.string().trim().min(1).max(80),
  currency: currencySchema,
  requestedAmount: positiveAmountSchema,
  requestedTenureMonths: z.number().int().min(1).max(60),
  reason: z.string().trim().max(500).optional(),
});

export const updateLoanRequestBodySchema = createLoanRequestBodySchema.partial().extend({
  expectedVersion: z.number().int().positive(),
});

export const recommendLoanRequestBodySchema = z.object({
  decision: z.enum(['recommend', 'decline']),
  note: z.string().trim().max(500).optional(),
});

/** Mirrors `decideExpenseClaimBodySchema` — same decision shape, same optional note. */
export const decideLoanRequestBodySchema = z.object({
  comment: z.string().max(500).optional(),
});

export const withdrawLoanRequestBodySchema = z.object({
  reason: z.string().trim().max(500).optional(),
});

export const listLoanRequestsQuerySchema = paginationQuerySchema.extend({
  employmentId: uuidSchema.optional(),
  status: payrollLoanRequestStatusSchema.optional(),
  /** `mine`: the caller's own requests. `team`: requests from the caller's reporting tree, awaiting
   * their recommendation. `all`: requires `loan:manage`. */
  scope: z.enum(['mine', 'team', 'all']).default('mine'),
});

// ---------------------------------------------------------------------------
// Loans & instalments
// ---------------------------------------------------------------------------

export const payrollLoanInstallmentSchema = z.object({
  id: uuidSchema,
  loanId: uuidSchema,
  installmentNo: z.number().int().positive(),
  cycleId: uuidSchema,
  /** e.g. "Oct 2024" — display only, resolved from the cycle at read time. */
  cyclePeriodLabel: z.string(),
  amount: decimalStringSchema,
  status: payrollLoanInstallmentStatusSchema,
  /** Set only when the payroll run carrying this instalment actually commits — never on creation,
   * and never on a draft/locked-but-unapproved run. */
  recoveredInRunId: uuidSchema.nullable(),
  recoveredAt: instantSchema.nullable(),
});
export type PayrollLoanInstallment = z.infer<typeof payrollLoanInstallmentSchema>;

export const payrollLoanSchema = z.object({
  id: uuidSchema,
  orgId: orgIdSchema,
  /** The originating request's reference — one number the employee already knows. */
  reference: z.string().nullable(),
  employmentId: uuidSchema,
  employeeName: z.string(),
  employeeCode: z.string().nullable(),
  requestId: uuidSchema.nullable(),
  loanType: z.string(),
  currency: currencySchema,
  principal: decimalStringSchema,
  tenureMonths: z.number().int().positive(),
  /** Instalments actually recovered through a committed payroll run — drives "N months left". */
  recoveredInstallments: z.number().int().nonnegative(),
  /** `principal / tenureMonths`, floored to the currency's minor unit — the final instalment absorbs
   * the remainder, so `monthlyInstallment * tenureMonths` is never exactly `principal` by itself. */
  monthlyInstallment: decimalStringSchema,
  /** The only source of truth for what is still owed — never derived from counting instalment rows
   * client-side, since a reversed recovery must be reflected here immediately. */
  outstandingPrincipal: decimalStringSchema,
  status: payrollLoanStatusSchema,
  disbursedAt: instantSchema.nullable(),
  disbursedByName: z.string().nullable(),
  closedAt: instantSchema.nullable(),
  closureNote: z.string().nullable(),
  createdAt: instantSchema,
  version: z.number().int().positive(),
});
export type PayrollLoan = z.infer<typeof payrollLoanSchema>;

export const listLoansQuerySchema = paginationQuerySchema.extend({
  employmentId: uuidSchema.optional(),
  status: payrollLoanStatusSchema.optional(),
  q: z.string().trim().max(120).optional(),
});

export const disburseLoanBodySchema = z.object({
  firstRecoveryCycleId: uuidSchema,
  note: z.string().trim().max(500).optional(),
});

/** Write-off — mandatory note, same shape as reimbursement's planned `closeAdvance`. */
export const closeLoanBodySchema = z.object({
  expectedVersion: z.number().int().positive(),
  closureNote: z.string().trim().min(1).max(500),
});

// ---------------------------------------------------------------------------
// Contract
// ---------------------------------------------------------------------------

const requestParams = z.object({ requestId: uuidSchema });
const loanParams = z.object({ loanId: uuidSchema });

export const payrollLoansContract = c.router(
  {
    listLoanRequests: {
      method: 'GET',
      path: '/requests',
      query: listLoanRequestsQuerySchema,
      responses: {
        200: paginatedSchema(payrollLoanRequestSchema),
        401: errorBodySchema,
        403: errorBodySchema,
      },
      summary: 'List loan/advance requests, scoped to self, team, or all',
      metadata: routeMeta(null),
    },

    createLoanRequest: {
      method: 'POST',
      path: '/requests',
      headers: idempotencyHeadersSchema,
      body: createLoanRequestBodySchema,
      responses: {
        201: payrollLoanRequestSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        422: errorBodySchema,
      },
      summary: 'Draft a loan/advance request',
      metadata: routeMeta('payroll.loan:request'),
    },

    updateLoanRequest: {
      method: 'PATCH',
      path: '/requests/:requestId',
      pathParams: requestParams,
      body: updateLoanRequestBodySchema,
      responses: {
        200: payrollLoanRequestSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        /** Stale `expectedVersion`, or the request is not `draft`. */
        409: errorBodySchema,
      },
      summary: 'Edit a draft request',
      metadata: routeMeta(null),
    },

    submitLoanRequest: {
      method: 'POST',
      path: '/requests/:requestId/submit',
      pathParams: requestParams,
      headers: idempotencyHeadersSchema,
      body: z.object({ expectedVersion: z.number().int().positive() }),
      responses: {
        200: payrollLoanRequestSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
        /** The requested amount/tenure exceeds `PayrollLoanEwaSettings`' configured cap. */
        422: errorBodySchema,
      },
      summary: 'Submit a draft request — starts the manager-recommendation/approval workflow',
      metadata: routeMeta(null),
    },

    withdrawLoanRequest: {
      method: 'POST',
      path: '/requests/:requestId/withdraw',
      pathParams: requestParams,
      headers: idempotencyHeadersSchema,
      body: withdrawLoanRequestBodySchema,
      responses: {
        200: payrollLoanRequestSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
      },
      summary: 'The requester pulling their own request back before a final decision',
      metadata: routeMeta(null),
    },

    recommendLoanRequest: {
      method: 'POST',
      path: '/requests/:requestId/recommend',
      pathParams: requestParams,
      headers: idempotencyHeadersSchema,
      body: recommendLoanRequestBodySchema,
      responses: {
        200: payrollLoanRequestSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
      },
      summary: "A manager's recommendation — never a disbursement, and never the final decision",
      metadata: routeMeta('payroll.loan:recommend'),
    },

    approveLoanRequest: {
      method: 'POST',
      path: '/requests/:requestId/approve',
      pathParams: requestParams,
      headers: idempotencyHeadersSchema,
      body: decideLoanRequestBodySchema,
      responses: {
        200: payrollLoanRequestSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
      },
      summary: 'The configured approval step — authorises the money, does not move it',
      metadata: routeMeta(null),
    },

    rejectLoanRequest: {
      method: 'POST',
      path: '/requests/:requestId/reject',
      pathParams: requestParams,
      headers: idempotencyHeadersSchema,
      body: decideLoanRequestBodySchema,
      responses: {
        200: payrollLoanRequestSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
      },
      summary: 'Reject a submitted request',
      metadata: routeMeta(null),
    },

    listLoans: {
      method: 'GET',
      path: '/loans',
      query: listLoansQuerySchema,
      responses: {
        200: paginatedSchema(payrollLoanSchema),
        401: errorBodySchema,
        403: errorBodySchema,
      },
      summary: 'List confirmed loans/advances',
      metadata: routeMeta(null),
    },

    getLoan: {
      method: 'GET',
      path: '/loans/:loanId',
      pathParams: loanParams,
      responses: {
        200: payrollLoanSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'One loan',
      metadata: routeMeta(null),
    },

    disburseLoan: {
      method: 'POST',
      path: '/loans/:loanId/disburse',
      pathParams: loanParams,
      headers: idempotencyHeadersSchema,
      body: disburseLoanBodySchema,
      responses: {
        200: payrollLoanSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        /** The loan is not `pending_disbursement`. */
        409: errorBodySchema,
      },
      summary: 'Confirm disbursement and generate the instalment schedule against real pay cycles',
      metadata: routeMeta(null),
    },

    listLoanInstallments: {
      method: 'GET',
      path: '/loans/:loanId/installments',
      pathParams: loanParams,
      responses: {
        200: z.object({ items: z.array(payrollLoanInstallmentSchema) }),
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: "A loan's instalment schedule and each one's actual recovery status",
      metadata: routeMeta(null),
    },

    closeLoan: {
      method: 'POST',
      path: '/loans/:loanId/close',
      pathParams: loanParams,
      headers: idempotencyHeadersSchema,
      body: closeLoanBodySchema,
      responses: {
        200: payrollLoanSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        /** Stale `expectedVersion`, or the loan is not `active`. */
        409: errorBodySchema,
      },
      summary: 'Write off a stale loan — mandatory reason, zeroes outstanding principal',
      metadata: routeMeta(null),
    },
  },
  { pathPrefix: '/payroll/loans' },
);
